import { PeriodPlan } from '@/optimizer/periodPlan.type';
import {
    normalizePeriodDate,
    toFiniteNumber,
} from '@/optimizer/helpers/periodPlanUtils';
import { OptimizedPlans } from '@analytical-alley/opt-wasm/optimizer/optimizer.interface';
import { getColumnValues, getColumnNames } from '@analytical-alley/opt-wasm';

function buildWeeklyCoefficients(
    plan: OptimizedPlans,
    numWeeks: number,
): Array<{ coeffSum: number; periodDate: string | undefined }> {
    const numOptRows = plan.optimizedPlan.numRows;

    let rawCoefficients: number[] = [];
    rawCoefficients = getColumnValues<number>(plan.optimizedPlan, 'Coefficient');
   

    let rawPeriodNames: unknown[] = [];
    rawPeriodNames = getColumnValues<unknown>(plan.optimizedPlan, 'Period name');
 

    if (rawPeriodNames.length === numOptRows && numOptRows > 0) {
        const orderedKeys: number[] = [];
        const groups: Record<number, { coeffSum: number; firstPeriodName: unknown }> = {};

        for (let i = 0; i < numOptRows; i++) {
            const isoWeek = getISOWeekOfYear(rawPeriodNames[i]);
            if (isoWeek == null) continue;
            if (!groups[isoWeek]) {
                groups[isoWeek] = { coeffSum: 0, firstPeriodName: rawPeriodNames[i] };
                orderedKeys.push(isoWeek);
            }
            const coeff = toFiniteNumber(rawCoefficients[i]);
            if (coeff != null) {
                groups[isoWeek]!.coeffSum += coeff;
            }
        }

        return orderedKeys.map((key) => {
            const g = groups[key]!;
            return {
                coeffSum: g.coeffSum,
                periodDate: normalizePeriodDate(g.firstPeriodName) ?? undefined,
            };
        });
    }

    const chunkSize = Math.ceil(numOptRows / numWeeks) || 1;
    return Array.from({ length: numWeeks }, (_, i) => {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, numOptRows);
        let sum = 0;
        for (let j = start; j < end; j++) {
            const c = toFiniteNumber(rawCoefficients[j]);
            if (c != null) { sum += c; }
        }
        return {
            coeffSum: sum,
            periodDate: normalizePeriodDate(rawPeriodNames[start]) ?? undefined,
        };
    });
}

function getISOWeekOfYear(value: unknown): number | null {
    let ms: number | null = null;

    if (value instanceof Date) {
        ms = value.getTime();
    } else if (typeof value === 'string') {
        const trimmed = value.trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
            const d = new Date(trimmed + 'T00:00:00Z');
            if (!Number.isNaN(d.getTime())) ms = d.getTime();
        }
    } else if (typeof value === 'number' && Number.isFinite(value)) {
        if (Math.abs(value) < 100_000) {
            ms = value * 86_400_000;
        } else if (Math.abs(value) < 1_000_000_000_000) {
            ms = value * 1000;
        } else {
            ms = value;
        }
    } else if (typeof value === 'bigint') {
        ms = Number(value / BigInt(1_000_000));
    }

    if (ms === null) return null;
    const date = new Date(ms);
    if (Number.isNaN(date.getTime())) return null;

    const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    const day = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}

export const modifyWeeklyPlan = (plan: OptimizedPlans): PeriodPlan[] => {
    const columnNames = getColumnNames(plan.weekly);
    const numRows = plan.weekly.numRows;

    const columnData: Record<string, any[]> = {};
    columnNames.forEach((colName) => {
        try {
            columnData[colName] = getColumnValues(plan.weekly, colName);
        } catch {
            columnData[colName] = new Array(numRows).fill(undefined);
        }
    });

    const rows: any[] = Array.from({ length: numRows }, (_, i) => {
        const row: any = {};
        columnNames.forEach((colName) => { row[colName] = columnData[colName][i]; });
        return row;
    });

    const weeklyCoefficients = buildWeeklyCoefficients(plan, numRows);

    return rows.map((row, index) => {
        const transformed: any = { ...row };

        transformed.periodIndex =
            toFiniteNumber(transformed['Week']) ??
            toFiniteNumber(transformed['ISO Week of Year']) ??
            index + 1;

        const entry = weeklyCoefficients[index];
        transformed.coefficient = entry?.coeffSum ?? 0;
        transformed.periodDate =
            entry?.periodDate ??
            normalizePeriodDate(transformed['Week starting'] ?? transformed['Period name']) ??
            transformed['Week starting'];

        delete transformed['Week'];
        delete transformed['ISO Week of Year'];
        delete transformed['Week starting'];
        delete transformed['Period name'];
        delete transformed['Coefficient'];

        return transformed as PeriodPlan;
    });
};
